import { useNavigate } from 'react-router-dom';
import { CommandMenu, type CommandItem } from '@shining-technologies/ui';
import { useUiStore } from '@/stores/ui';
import { usePermission } from '@/hooks/usePermission';
import { ALL_NAV_ITEMS, reachableNavItems } from '@/app/nav';
import { useTheme } from '@/hooks/useTheme';

const NAVIGATE = 'Navigate';
const QUICK_ACTIONS = 'Quick actions';

/**
 * ⌘K command palette: navigation + quick actions, on the kit `CommandMenu`
 * (which owns the ⌘K / Ctrl+K toggle, focus trap and focus return). Open state
 * is `useUiStore.commandPaletteOpen`, so the header's Search button and the `/`
 * shortcut open it too. Actions are permission-gated via the same matrix as
 * the sidebar (no hardcoded roles).
 *
 * FIX(admin-command-palette): Before: no DialogTitle, so the dialog had no
 * accessible name (and Radix logged a console error). After: the kit renders a
 * visually-hidden title from `title` ("Command palette").
 */
export function CommandPalette() {
  const open = useUiStore((s) => s.commandPaletteOpen);
  const setOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const setShortcutsOpen = useUiStore((s) => s.setShortcutsOpen);
  const navigate = useNavigate();
  const { can } = usePermission();
  const { setPref } = useTheme();

  // The kit closes the menu after any pick (`closeOnSelect`), so each item only
  // does its own work.
  const navItems: CommandItem[] = reachableNavItems(ALL_NAV_ITEMS, (a) => can(a)).map((item) => {
    const Icon = item.icon;
    return {
      id: `nav:${item.path}`,
      label: item.label,
      group: NAVIGATE,
      icon: <Icon aria-hidden />,
      description: item.description,
      // The legacy palette matched the description too; the kit searches only
      // label, group and keywords.
      keywords: [item.description],
      onSelect: () => navigate(item.path),
    };
  });

  const action = (id: string, label: string, onSelect: () => void): CommandItem => ({
    id: `action:${id}`,
    label,
    group: QUICK_ACTIONS,
    onSelect,
  });

  // FIX(admin-command-palette): Before: "Invite user…"
  // (/users?action=invite) and "Create workspace…"
  // (/workspaces?action=create) — no page read `action`, so they
  // only navigated, and `action` leaked into the list's API
  // filters. After: removed; each page has its own create button
  // (and `action` is a reserved list param).
  const quickActions: CommandItem[] = [
    ...(can('integrations:resync') ? [action('resync', 'Resync integration…', () => navigate('/integrations'))] : []),
    ...(can('audit:view') ? [action('audit', 'Open audit log…', () => navigate('/audit'))] : []),
    ...(can('notifications:view') ? [action('queues', 'Open job queues…', () => navigate('/system?tab=queues'))] : []),
    action('dark', 'Toggle dark mode', () => setPref('dark')),
    action('light', 'Toggle light mode', () => setPref('light')),
    action('shortcuts', 'Show keyboard shortcuts', () => setShortcutsOpen(true)),
  ];

  return (
    <CommandMenu
      open={open}
      onOpenChange={setOpen}
      title="Command palette"
      inputLabel="Search pages and actions"
      placeholder="Search pages and actions…"
      emptyMessage="No results found."
      items={[...navItems, ...quickActions]}
    />
  );
}
